import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import Tabs from "@galaxy-io/dls/navigation/Tabs";
import Timeline from "@galaxy-io/dls/timeline/Timeline";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { DrawerSection, DrawerStack } from "@/components/workspace/drawer-content";
import { Inset } from "@/components/workspace/people-styles";
import { RecordAudit } from "@/components/workspace/record-audit";
import { apiClient, unwrap } from "@/lib/api-client";
import { date } from "@/lib/utils";

export function IntegrationDrawerContent({
  integrationId,
  children,
}: {
  integrationId?: string;
  children: ReactNode;
}) {
  const { params, update } = useUrlState();
  const tab = params.get("integrationTab") === "audit" ? "audit" : "overview";
  return (
    <>
      {integrationId && (
        <Tabs
          ariaLabel="Integration sections"
          value={tab}
          onChange={(value) => update({ integrationTab: value === "overview" ? null : value })}
          items={[
            { id: "overview", label: "Overview" },
            { id: "audit", label: "Audit" },
          ]}
        />
      )}
      <DrawerStack>
        {integrationId && tab === "audit" ? (
          <>
            <RecordAudit kind="integration" id={integrationId} />
            <TrackingAudit id={integrationId} />
          </>
        ) : (
          children
        )}
      </DrawerStack>
    </>
  );
}

function TrackingAudit({ id }: { id: string }) {
  const events = useQuery({
    queryKey: ["integration-audit", id],
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/integrations/{id}/tracking-events", {
          params: { path: { id } },
          signal,
        }),
      ),
  });
  return (
    <DrawerSection header="Tracking history" icon={ClockCounterClockwiseIcon}>
      {events.isPending || events.error ? (
        <QueryState
          error={events.error}
          loadingMessage="Loading history…"
          onRetry={() => void events.refetch()}
        />
      ) : events.data?.length ? (
        <Timeline
          aria-label="Tracking changes"
          items={events.data.map((event) => ({
            id: event.id,
            label: `${event.actorName || "Unknown user"} stopped tracking ${event.name}`,
            time: date(event.createdAt, true),
            dateTime: event.createdAt,
          }))}
        />
      ) : (
        <Inset>
          <EmptyState
            header="No tracking changes yet"
            description="Changes to tracked sources appear here."
          />
        </Inset>
      )}
    </DrawerSection>
  );
}
